import {
  WELCOME_PLATFORMS,
  type WelcomeSection,
  type WelcomeSectionOf,
  type WelcomeSectionType,
  type WelcomePlatform,
} from '@utazzo/shared';
import { UzInput, UzRadioGroup, cn } from '@utazzo/ui';
import {
  BarChart3,
  Columns2,
  Flag,
  Images,
  LayoutGrid,
  ListOrdered,
  Megaphone,
  PanelTop,
  Star,
  Type,
  Users,
  type LucideIcon,
} from 'lucide-react';
import React from 'react';

import { AreaField, FieldGrid, IconSelect, ListEditor, TextField } from './editor-fields';
import { WelcomeIcon } from './WelcomeIcon';

import { MediaDropField } from '@/features/website-cms/components/MediaDropField';
import { MediaGalleryField } from '@/features/website-cms/components/MediaGalleryField';

const FOLDER = 'welcome-page';

type S<T extends WelcomeSectionType> = WelcomeSectionOf<T>;
type FormProps<T extends WelcomeSectionType> = { value: S<T>; onChange: (next: S<T>) => void };

/** Label / hint / icon for every section type (the "Add section" catalogue). */
export const WELCOME_SECTION_META: Record<
  WelcomeSectionType,
  { label: string; hint: string; icon: LucideIcon }
> = {
  hero: {
    label: 'Hero banner',
    hint: 'Designed banner image, or big title + pillars',
    icon: PanelTop,
  },
  text: { label: 'Text', hint: 'Heading with paragraphs', icon: Type },
  twoColumn: {
    label: 'Two-column card',
    hint: 'Side-by-side card, e.g. Vision & Mission',
    icon: Columns2,
  },
  photos: {
    label: 'Photo carousel',
    hint: 'Heading, text, sliding photos and a button',
    icon: Images,
  },
  people: { label: 'People with quotes', hint: 'Photo, name, role and a quote', icon: Users },
  campaigns: {
    label: 'Campaigns',
    hint: 'Destination circles + running creatives',
    icon: Megaphone,
  },
  stats: { label: 'Stats row', hint: 'Icon, number and label strip', icon: BarChart3 },
  cards: {
    label: 'Icon cards',
    hint: 'Grid of cards with icon, title and tagline',
    icon: LayoutGrid,
  },
  stars: { label: 'Stars slider', hint: 'Celebrity cards (website or custom)', icon: Star },
  list: { label: 'List', hint: 'Points with a coloured rule or numbered cards', icon: ListOrdered },
  banner: { label: 'Closing banner', hint: 'Dark banner with a big yellow line', icon: Flag },
};

/** One-line summary shown on the section row. */
export const sectionSummary = (s: WelcomeSection): string => {
  switch (s.type) {
    case 'hero':
      return s.imageMediaId
        ? 'Image banner'
        : [s.titleTop, s.titleBottom].filter(Boolean).join(' ');
    case 'twoColumn':
      return [s.leftTitle, s.rightTitle].filter(Boolean).join(' / ');
    case 'banner':
      return s.title;
    default:
      return 'heading' in s ? s.heading : '';
  }
};

// ---------------------------------------------------------------------------

const HeroForm = ({ value: v, onChange }: FormProps<'hero'>): React.JSX.Element => {
  const set = (p: Partial<S<'hero'>>) => onChange({ ...v, ...p });
  return (
    <>
      <div className="rounded-[var(--r-md)] bg-[var(--z-50)] p-3 text-sm text-[var(--fg-muted)]">
        Upload the designed banner to show it as-is. Without an image, the page draws the banner
        from the text below.
      </div>
      <FieldGrid>
        <MediaDropField
          label="Banner image (desktop)"
          value={v.imageMediaId}
          onChange={(id) => set({ imageMediaId: id })}
          folder={FOLDER}
          variant="banner"
        />
        <MediaDropField
          label="Banner image (phone, optional)"
          value={v.mobileImageMediaId}
          onChange={(id) => set({ mobileImageMediaId: id })}
          folder={FOLDER}
          variant="banner"
        />
      </FieldGrid>
      <FieldGrid>
        <TextField
          label="Title line 1"
          value={v.titleTop}
          max={40}
          onChange={(titleTop) => set({ titleTop })}
        />
        <TextField
          label="Title line 2 (last word turns yellow)"
          value={v.titleBottom}
          max={40}
          onChange={(titleBottom) => set({ titleBottom })}
        />
        <TextField
          label="Tagline"
          value={v.tagline}
          max={160}
          onChange={(tagline) => set({ tagline })}
        />
        <TextField
          label="Tagline accent (yellow)"
          value={v.taglineAccent}
          max={60}
          onChange={(taglineAccent) => set({ taglineAccent })}
        />
        <TextField
          label="Footer line"
          value={v.footerLine}
          max={80}
          onChange={(footerLine) => set({ footerLine })}
        />
        <TextField
          label="Footer accent (yellow)"
          value={v.footerAccent}
          max={60}
          onChange={(footerAccent) => set({ footerAccent })}
        />
      </FieldGrid>
      <h4 className="text-sm font-semibold">Pillars</h4>
      <ListEditor
        items={v.pillars}
        onChange={(pillars) => set({ pillars })}
        max={6}
        addLabel="Add pillar"
        itemLabel={(p) => p.title}
        newItem={() => ({ title: '', body: '' })}
        renderItem={(p, update) => (
          <FieldGrid>
            <TextField
              label="Title"
              value={p.title}
              max={40}
              onChange={(title) => update({ title })}
            />
            <TextField label="Line" value={p.body} max={80} onChange={(body) => update({ body })} />
          </FieldGrid>
        )}
      />
    </>
  );
};

const TextForm = ({ value: v, onChange }: FormProps<'text'>): React.JSX.Element => (
  <>
    <TextField
      label="Heading"
      value={v.heading}
      max={120}
      onChange={(heading) => onChange({ ...v, heading })}
    />
    <AreaField
      label="Text"
      value={v.body}
      max={5000}
      rows={10}
      hint="Leave a blank line between paragraphs."
      onChange={(body) => onChange({ ...v, body })}
    />
  </>
);

const TwoColumnForm = ({ value: v, onChange }: FormProps<'twoColumn'>): React.JSX.Element => {
  const set = (p: Partial<S<'twoColumn'>>) => onChange({ ...v, ...p });
  return (
    <FieldGrid>
      <div className="grid gap-3">
        <TextField
          label="Left title"
          value={v.leftTitle}
          max={60}
          onChange={(leftTitle) => set({ leftTitle })}
        />
        <AreaField label="Left text" value={v.left} max={600} onChange={(left) => set({ left })} />
      </div>
      <div className="grid gap-3">
        <TextField
          label="Right title"
          value={v.rightTitle}
          max={60}
          onChange={(rightTitle) => set({ rightTitle })}
        />
        <AreaField
          label="Right text"
          value={v.right}
          max={600}
          onChange={(right) => set({ right })}
        />
      </div>
    </FieldGrid>
  );
};

const PhotosForm = ({ value: v, onChange }: FormProps<'photos'>): React.JSX.Element => {
  const set = (p: Partial<S<'photos'>>) => onChange({ ...v, ...p });
  return (
    <>
      <TextField
        label="Heading"
        value={v.heading}
        max={120}
        onChange={(heading) => set({ heading })}
      />
      <AreaField label="Text" value={v.body} max={800} onChange={(body) => set({ body })} />
      <MediaGalleryField
        label="Photos (drag to reorder)"
        value={v.photoMediaIds}
        onChange={(photoMediaIds) => set({ photoMediaIds })}
        folder={FOLDER}
        maxItems={30}
      />
      <FieldGrid>
        <TextField
          label="Button label"
          value={v.ctaLabel}
          max={60}
          onChange={(ctaLabel) => set({ ctaLabel })}
        />
        <TextField
          label="Button link"
          value={v.ctaHref}
          max={500}
          placeholder="https://instagram.com/…"
          onChange={(ctaHref) => set({ ctaHref })}
        />
      </FieldGrid>
    </>
  );
};

const PeopleForm = ({ value: v, onChange }: FormProps<'people'>): React.JSX.Element => {
  const set = (p: Partial<S<'people'>>) => onChange({ ...v, ...p });
  return (
    <>
      <FieldGrid>
        <TextField
          label="Heading"
          value={v.heading}
          max={120}
          onChange={(heading) => set({ heading })}
        />
        <TextField
          label="Subheading"
          value={v.subheading}
          max={160}
          onChange={(subheading) => set({ subheading })}
        />
      </FieldGrid>
      <ListEditor
        items={v.items}
        onChange={(items) => set({ items })}
        max={12}
        addLabel="Add person"
        itemLabel={(f) => f.name}
        newItem={() => ({ name: '', role: '', photoMediaId: null, quote: '' })}
        renderItem={(f, update) => (
          <div className="grid gap-3 sm:grid-cols-[160px_1fr]">
            <MediaDropField
              label="Photo"
              value={f.photoMediaId}
              onChange={(photoMediaId) => update({ photoMediaId })}
              folder={FOLDER}
              variant="square"
            />
            <div className="grid gap-3">
              <FieldGrid>
                <TextField
                  label="Name"
                  value={f.name}
                  max={80}
                  onChange={(name) => update({ name })}
                />
                <TextField
                  label="Role"
                  value={f.role}
                  max={120}
                  onChange={(role) => update({ role })}
                />
              </FieldGrid>
              <AreaField
                label="Quote"
                value={f.quote}
                max={1200}
                rows={4}
                onChange={(quote) => update({ quote })}
              />
            </div>
          </div>
        )}
      />
    </>
  );
};

const PlatformPicker = ({
  value,
  onChange,
}: {
  value: WelcomePlatform[];
  onChange: (v: WelcomePlatform[]) => void;
}): React.JSX.Element => (
  <div>
    <p className="mb-1.5 text-sm font-medium">Platforms</p>
    <div className="flex flex-wrap gap-1.5">
      {WELCOME_PLATFORMS.map((p) => {
        const on = value.includes(p);
        return (
          <button
            key={p}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(on ? value.filter((x) => x !== p) : [...value, p])}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs capitalize transition',
              on
                ? 'border-[#093F51] bg-[#093F51] text-white'
                : 'border-[var(--ds-border)] text-[var(--fg-muted)] hover:bg-[var(--z-50)]',
            )}
          >
            <WelcomeIcon name={p} className="h-3.5 w-3.5" />
            {p}
          </button>
        );
      })}
    </div>
  </div>
);

const CampaignsForm = ({ value: v, onChange }: FormProps<'campaigns'>): React.JSX.Element => {
  const set = (p: Partial<S<'campaigns'>>) => onChange({ ...v, ...p });
  return (
    <>
      <FieldGrid>
        <TextField
          label="Heading"
          value={v.heading}
          max={120}
          onChange={(heading) => set({ heading })}
        />
        <TextField
          label="Subheading"
          value={v.subheading}
          max={160}
          onChange={(subheading) => set({ subheading })}
        />
        <TextField
          label="View more label"
          value={v.viewMoreLabel}
          max={40}
          onChange={(viewMoreLabel) => set({ viewMoreLabel })}
        />
        <TextField
          label="View more link (used once every creative is shown)"
          value={v.viewMoreHref}
          max={500}
          onChange={(viewMoreHref) => set({ viewMoreHref })}
        />
      </FieldGrid>

      <h4 className="text-sm font-semibold">Destination circles</h4>
      <p className="-mt-2 text-xs text-[var(--fg-faint)]">
        Clicking a circle filters the creatives whose destination matches its name.
      </p>
      <ListEditor
        items={v.destinations}
        onChange={(destinations) => set({ destinations })}
        max={30}
        addLabel="Add destination"
        itemLabel={(d) => d.name}
        newItem={() => ({ name: '', imageMediaId: null })}
        renderItem={(d, update) => (
          <div className="grid gap-3 sm:grid-cols-[140px_1fr]">
            <MediaDropField
              label="Circle image"
              value={d.imageMediaId}
              onChange={(imageMediaId) => update({ imageMediaId })}
              folder={FOLDER}
              variant="square"
            />
            <TextField label="Name" value={d.name} max={60} onChange={(name) => update({ name })} />
          </div>
        )}
      />

      <h4 className="text-sm font-semibold">Creatives</h4>
      <ListEditor<S<'campaigns'>['items'][number]>
        items={v.items}
        onChange={(items) => set({ items })}
        max={60}
        addLabel="Add creative"
        itemLabel={(c) => [c.destination, c.startedOn].filter(Boolean).join(' · ')}
        newItem={() => ({
          destination: '',
          imageMediaId: null,
          startedOn: new Date().toISOString().slice(0, 10),
          platforms: ['instagram'],
          href: '',
        })}
        renderItem={(c, update) => (
          <div className="grid gap-3 sm:grid-cols-[180px_1fr]">
            <MediaDropField
              label="Creative"
              value={c.imageMediaId}
              onChange={(imageMediaId) => update({ imageMediaId })}
              folder={FOLDER}
              variant="square"
            />
            <div className="grid gap-3">
              <FieldGrid>
                <TextField
                  label="Destination"
                  value={c.destination}
                  max={60}
                  onChange={(destination) => update({ destination })}
                />
                <UzInput
                  label="Started running on"
                  type="date"
                  value={c.startedOn ?? ''}
                  onChange={(e) => update({ startedOn: e.target.value || null })}
                />
              </FieldGrid>
              <PlatformPicker value={c.platforms} onChange={(platforms) => update({ platforms })} />
              <TextField
                label="Link to the post / ad (optional)"
                value={c.href}
                max={500}
                onChange={(href) => update({ href })}
              />
            </div>
          </div>
        )}
      />
    </>
  );
};

const StatsForm = ({ value: v, onChange }: FormProps<'stats'>): React.JSX.Element => (
  <>
    <TextField
      label="Heading"
      value={v.heading}
      max={120}
      onChange={(heading) => onChange({ ...v, heading })}
    />
    <ListEditor<S<'stats'>['items'][number]>
      items={v.items}
      onChange={(items) => onChange({ ...v, items })}
      max={12}
      addLabel="Add stat"
      itemLabel={(s) => `${s.value} ${s.label}`}
      newItem={() => ({ icon: 'star', value: '', label: '' })}
      renderItem={(s, update) => (
        <div className="grid gap-3 sm:grid-cols-3">
          <IconSelect value={s.icon} onChange={(icon) => update({ icon })} />
          <TextField
            label="Value"
            value={s.value}
            max={40}
            onChange={(value) => update({ value })}
          />
          <TextField
            label="Label"
            value={s.label}
            max={60}
            onChange={(label) => update({ label })}
          />
        </div>
      )}
    />
  </>
);

const CardsForm = ({ value: v, onChange }: FormProps<'cards'>): React.JSX.Element => (
  <>
    <TextField
      label="Heading (optional)"
      value={v.heading}
      max={120}
      onChange={(heading) => onChange({ ...v, heading })}
    />
    <ListEditor<S<'cards'>['items'][number]>
      items={v.items}
      onChange={(items) => onChange({ ...v, items })}
      max={12}
      addLabel="Add card"
      itemLabel={(x) => x.title}
      newItem={() => ({ icon: 'star', iconMediaId: null, title: '', subtitle: '', tagline: '' })}
      renderItem={(x, update) => (
        <>
          <div className="grid gap-3 sm:grid-cols-[140px_1fr]">
            <MediaDropField
              label="Upload icon / logo"
              value={x.iconMediaId}
              onChange={(iconMediaId) => update({ iconMediaId })}
              folder={FOLDER}
              variant="square"
            />
            <div className="grid content-start gap-3">
              <IconSelect value={x.icon} onChange={(icon) => update({ icon })} />
              <p className="-mt-1 text-xs text-[var(--fg-faint)]">
                An uploaded icon replaces the built-in one. Shown at 62 x 59px - a square,
                transparent PNG or SVG works best.
              </p>
              <TextField
                label="Title"
                value={x.title}
                max={60}
                onChange={(title) => update({ title })}
              />
            </div>
          </div>
          <FieldGrid>
            <TextField
              label="Subtitle"
              value={x.subtitle}
              max={120}
              onChange={(subtitle) => update({ subtitle })}
            />
            <TextField
              label="Tagline (italic)"
              value={x.tagline}
              max={200}
              onChange={(tagline) => update({ tagline })}
            />
          </FieldGrid>
        </>
      )}
    />
  </>
);

const StarsForm = ({ value: v, onChange }: FormProps<'stars'>): React.JSX.Element => {
  const set = (p: Partial<S<'stars'>>) => onChange({ ...v, ...p });
  return (
    <>
      <TextField
        label="Heading"
        value={v.heading}
        max={120}
        onChange={(heading) => set({ heading })}
      />
      <AreaField label="Text" value={v.body} max={400} onChange={(body) => set({ body })} />
      <UzRadioGroup<'website' | 'custom'>
        label="Cards"
        value={v.source}
        onValueChange={(source) => set({ source })}
        options={[
          {
            value: 'website',
            label: 'Same as the website',
            description:
              'Reuses the “Stars Travelled With Us” cards managed in Website CMS → Home.',
          },
          { value: 'custom', label: 'Custom list for this section' },
        ]}
      />
      {v.source === 'custom' && (
        <ListEditor
          items={v.items}
          onChange={(items) => set({ items })}
          max={30}
          addLabel="Add star"
          itemLabel={(s) => s.name}
          newItem={() => ({ name: '', blurb: '', photoMediaId: null, bgColour: '#093F51' })}
          renderItem={(s, update) => (
            <div className="grid gap-3 sm:grid-cols-[160px_1fr]">
              <MediaDropField
                label="Photo (transparent PNG works best)"
                value={s.photoMediaId}
                onChange={(photoMediaId) => update({ photoMediaId })}
                folder={FOLDER}
                variant="square"
              />
              <div className="grid gap-3">
                <FieldGrid>
                  <TextField
                    label="Name"
                    value={s.name}
                    max={80}
                    onChange={(name) => update({ name })}
                  />
                  <div className="flex items-end gap-2">
                    <input
                      type="color"
                      aria-label="Card colour"
                      value={s.bgColour}
                      onChange={(e) => update({ bgColour: e.target.value })}
                      className="mb-0.5 h-9 w-10 shrink-0 cursor-pointer rounded border border-[var(--ds-border)]"
                    />
                    <UzInput
                      label="Card colour"
                      value={s.bgColour}
                      maxLength={7}
                      onChange={(e) => update({ bgColour: e.target.value })}
                      containerClassName="flex-1"
                    />
                  </div>
                </FieldGrid>
                <AreaField
                  label="About"
                  value={s.blurb}
                  max={280}
                  onChange={(blurb) => update({ blurb })}
                />
              </div>
            </div>
          )}
        />
      )}
    </>
  );
};

const ListForm = ({ value: v, onChange }: FormProps<'list'>): React.JSX.Element => (
  <>
    <TextField
      label="Heading"
      value={v.heading}
      max={120}
      onChange={(heading) => onChange({ ...v, heading })}
    />
    <UzRadioGroup<'lines' | 'numbered'>
      label="Style"
      orientation="horizontal"
      value={v.style}
      onValueChange={(style) => onChange({ ...v, style })}
      options={[
        { value: 'lines', label: 'Coloured lines' },
        { value: 'numbered', label: 'Numbered cards' },
      ]}
    />
    <ListEditor
      items={v.items}
      onChange={(items) => onChange({ ...v, items })}
      max={20}
      addLabel="Add point"
      itemLabel={(x) => x.title}
      newItem={() => ({ title: '', body: '' })}
      renderItem={(x, update) => (
        <>
          <TextField
            label="Title"
            value={x.title}
            max={120}
            onChange={(title) => update({ title })}
          />
          <AreaField label="Text" value={x.body} max={800} onChange={(body) => update({ body })} />
        </>
      )}
    />
  </>
);

const BannerForm = ({ value: v, onChange }: FormProps<'banner'>): React.JSX.Element => {
  const set = (p: Partial<S<'banner'>>) => onChange({ ...v, ...p });
  return (
    <>
      <FieldGrid>
        <TextField
          label="Top line"
          value={v.eyebrow}
          max={120}
          onChange={(eyebrow) => set({ eyebrow })}
        />
        <TextField
          label="Big title (yellow)"
          value={v.title}
          max={80}
          onChange={(title) => set({ title })}
        />
      </FieldGrid>
      <TextField
        label="Pillars (comma separated)"
        value={v.pillars.join(', ')}
        max={400}
        onChange={(t) =>
          set({
            pillars: t
              .split(',')
              .map((x) => x.trimStart().slice(0, 40))
              .slice(0, 8),
          })
        }
      />
      <div className="max-w-[200px]">
        <MediaDropField
          label="Left illustration (optional)"
          value={v.imageMediaId}
          onChange={(imageMediaId) => set({ imageMediaId })}
          folder={FOLDER}
          variant="square"
        />
      </div>
    </>
  );
};

/** The form for one section, picked by its type. */
export const WelcomeSectionForm = ({
  section: s,
  onChange,
}: {
  section: WelcomeSection;
  onChange: (next: WelcomeSection) => void;
}): React.JSX.Element => {
  switch (s.type) {
    case 'hero':
      return <HeroForm value={s} onChange={onChange} />;
    case 'text':
      return <TextForm value={s} onChange={onChange} />;
    case 'twoColumn':
      return <TwoColumnForm value={s} onChange={onChange} />;
    case 'photos':
      return <PhotosForm value={s} onChange={onChange} />;
    case 'people':
      return <PeopleForm value={s} onChange={onChange} />;
    case 'campaigns':
      return <CampaignsForm value={s} onChange={onChange} />;
    case 'stats':
      return <StatsForm value={s} onChange={onChange} />;
    case 'cards':
      return <CardsForm value={s} onChange={onChange} />;
    case 'stars':
      return <StarsForm value={s} onChange={onChange} />;
    case 'list':
      return <ListForm value={s} onChange={onChange} />;
    case 'banner':
      return <BannerForm value={s} onChange={onChange} />;
  }
};

/** Trim blank rows an editor may leave behind (an empty "Add" card). */
export const cleanSection = (s: WelcomeSection): WelcomeSection => {
  switch (s.type) {
    case 'hero':
      return { ...s, pillars: s.pillars.filter((p) => p.title.trim()) };
    case 'people':
      return { ...s, items: s.items.filter((x) => x.name.trim()) };
    case 'campaigns':
      return { ...s, destinations: s.destinations.filter((d) => d.name.trim()) };
    case 'stats':
      return { ...s, items: s.items.filter((x) => x.value.trim()) };
    case 'cards':
      return { ...s, items: s.items.filter((x) => x.title.trim()) };
    case 'stars':
      return { ...s, items: s.items.filter((x) => x.name.trim()) };
    case 'list':
      return { ...s, items: s.items.filter((x) => x.title.trim()) };
    case 'banner':
      return { ...s, pillars: s.pillars.map((p) => p.trim()).filter(Boolean) };
    default:
      return s;
  }
};
